import React from 'react';
import { LegalModel } from './useLegalEditor';

const LegalSectionsEditor: React.FC<{ model: LegalModel }> = ({ model }) => {
  const {
    versions, editingContent, expandedSection, setExpandedSection,
    showVersionHistory, setShowVersionHistory, addSection, updateSection,
    removeSection, moveSection,
  } = model;

  return (
    <div className="mb-4">
      <div className="flex justify-between items-center mb-3">
        <h3 className="text-sm font-semibold" style={{ color: 'var(--rp-text)' }}>
          Document Sections ({editingContent?.sections?.length || 0})
        </h3>
        <div className="flex gap-2">
          <button
            onClick={() => setShowVersionHistory(!showVersionHistory)}
            className="px-3 py-1.5 rounded-lg text-xs font-semibold transition-all hover:bg-white/5 border"
            style={{ borderColor: 'var(--rp-border)', color: 'var(--rp-text-secondary)' }}
          >
            {showVersionHistory ? 'Hide' : 'Show'} Version History
          </button>
          <button
            onClick={addSection}
            className="px-3 py-1.5 rounded-lg text-xs font-semibold transition-all hover:bg-white/5 border"
            style={{ borderColor: 'var(--rp-border)', color: 'var(--rp-text-secondary)' }}
          >
            + Add Section
          </button>
        </div>
      </div>

      {/* Version History */}
      {showVersionHistory && (
        <div className="mb-4 p-4 rounded-lg border" style={{ borderColor: 'var(--rp-border)', backgroundColor: 'rgba(255,255,255,0.02)' }}>
          <h4 className="text-sm font-semibold mb-3" style={{ color: 'var(--rp-text)' }}>Version History</h4>
          {versions.length === 0 ? (
            <p className="text-xs" style={{ color: 'var(--rp-text-tertiary)' }}>No published versions yet</p>
          ) : (
            <div className="space-y-2">
              {versions.map(v => (
                <div key={v.id} className="flex items-center gap-3 text-xs p-2 rounded" style={{ backgroundColor: 'rgba(255,255,255,0.03)' }}>
                  <span className={`px-2 py-0.5 rounded font-bold ${v.is_active ? 'bg-green-500/20 text-green-400' : 'bg-gray-500/20 text-gray-400'}`}>
                    v{v.version}
                  </span>
                  {v.requires_reconsent && (
                    <span className="px-2 py-0.5 rounded bg-orange-500/20 text-orange-400 font-semibold">
                      RE-CONSENT
                    </span>
                  )}
                  <span style={{ color: 'var(--rp-text-secondary)' }}>
                    {new Date(v.effective_date).toLocaleDateString()}
                  </span>
                  <span style={{ color: 'var(--rp-text-tertiary)' }}>
                    {v.summary || 'No summary'}
                  </span>
                  {v.is_active && (
                    <span className="ml-auto px-2 py-0.5 rounded bg-green-500/20 text-green-400 font-bold">ACTIVE</span>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Sections List */}
      <div className="space-y-2">
        {(editingContent?.sections || []).map((section) => (
          <div
            key={section.id}
            className="border rounded-lg overflow-hidden"
            style={{ borderColor: 'var(--rp-border)' }}
          >
            <button
              onClick={() => setExpandedSection(expandedSection === section.id ? null : section.id)}
              className="w-full flex items-center justify-between p-3 text-left hover:bg-white/5 transition-all"
            >
              <div className="flex items-center gap-2">
                <span className="text-xs" style={{ color: 'var(--rp-text-tertiary)' }}>
                  {expandedSection === section.id ? '▼' : '▶'}
                </span>
                <span className="text-sm font-semibold" style={{ color: 'var(--rp-text)' }}>
                  {section.title}
                </span>
              </div>
              <div className="flex items-center gap-1">
                <span className="text-xs px-2 py-0.5 rounded" style={{ backgroundColor: 'rgba(255,255,255,0.05)', color: 'var(--rp-text-tertiary)' }}>
                  {section.content.length} chars
                </span>
                <button
                  onClick={e => { e.stopPropagation(); moveSection(section.id, 'up'); }}
                  className="admin-icon-btn"
                  style={{ color: 'var(--rp-text-tertiary)' }}
                  aria-label={`Move section ${section.title} up`}
                >↑</button>
                <button
                  onClick={e => { e.stopPropagation(); moveSection(section.id, 'down'); }}
                  className="admin-icon-btn"
                  style={{ color: 'var(--rp-text-tertiary)' }}
                  aria-label={`Move section ${section.title} down`}
                >↓</button>
                <button
                  onClick={e => { e.stopPropagation(); removeSection(section.id); }}
                  className="admin-icon-btn"
                  style={{ color: 'var(--admin-error)' }}
                  aria-label={`Remove section ${section.title}`}
                >×</button>
              </div>
            </button>
            {expandedSection === section.id && (
              <div className="p-3 border-t" style={{ borderColor: 'var(--rp-border)', backgroundColor: 'rgba(255,255,255,0.02)' }}>
                <div className="mb-3">
                  <label htmlFor={`legal-sec-title-${section.id}`} className="block text-xs font-semibold mb-1" style={{ color: 'var(--rp-text-secondary)' }}>
                    Section Title
                  </label>
                  <input
                    id={`legal-sec-title-${section.id}`}
                    type="text"
                    value={section.title}
                    onChange={e => updateSection(section.id, 'title', e.target.value)}
                    className="w-full px-3 py-2 rounded-lg border bg-transparent text-sm"
                    style={{ borderColor: 'var(--rp-border)', color: 'var(--rp-text)' }}
                  />
                </div>
                <div>
                  <label htmlFor={`legal-sec-content-${section.id}`} className="block text-xs font-semibold mb-1" style={{ color: 'var(--rp-text-secondary)' }}>
                    Content (plain text, use bullet points with • prefix)
                  </label>
                  <textarea
                    id={`legal-sec-content-${section.id}`}
                    value={section.content}
                    onChange={e => updateSection(section.id, 'content', e.target.value)}
                    rows={10}
                    className="w-full px-3 py-2 rounded-lg border bg-transparent text-sm font-mono"
                    style={{ borderColor: 'var(--rp-border)', color: 'var(--rp-text)' }}
                  />
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default LegalSectionsEditor;
